const SITE_CODE_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;
const REQUIRED_ENDPOINTS = [
	'passive_start',
	'complete',
	'active_sync_start',
	'active_sync_complete',
	'logout',
	'access_denied_logout',
	'session_status',
	'session_expired',
	'login_path',
];

const disabledContext = Object.freeze({
	enabled: false,
	available: true,
	unified_login_enabled: false,
	passive_login_enabled: false,
	active_sync_enabled: false,
});

function unwrapResponse(payload) {
	if (payload && typeof payload === 'object' && payload.data && typeof payload.data === 'object') {
		return payload.data;
	}
	return payload;
}

function validateSameOriginPath(value, fieldName, origin) {
	if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) {
		throw new Error(`Invalid SSO ${fieldName}.`);
	}

	let parsed;
	try {
		parsed = new URL(value, origin);
	} catch (error) {
		throw new Error(`Invalid SSO ${fieldName}.`);
	}

	if (
		parsed.origin !== origin
		|| parsed.username !== ''
		|| parsed.password !== ''
		|| parsed.hash !== ''
		|| parsed.search !== ''
		|| parsed.pathname !== value
	) {
		throw new Error(`Invalid SSO ${fieldName}.`);
	}
	return value;
}

function validatePositiveSeconds(value, fieldName) {
	if (!Number.isInteger(value) || value <= 0 || value > 86400) {
		throw new Error(`Invalid SSO ${fieldName}.`);
	}
	return value;
}

function validatePortalContext(value, origin) {
	if (
		!value
		|| typeof value !== 'object'
		|| typeof value.enabled !== 'boolean'
		|| typeof value.available !== 'boolean'
	) {
		throw new Error('Invalid SSO public context.');
	}
	if (!value.available) {
		throw new Error('SSO public context is unavailable.');
	}
	if (!value.enabled) return disabledContext;

	if (
		typeof value.unified_login_enabled !== 'boolean'
		|| typeof value.passive_login_enabled !== 'boolean'
		|| typeof value.active_sync_enabled !== 'boolean'
		|| !value.site
		|| typeof value.site !== 'object'
		|| !SITE_CODE_PATTERN.test(value.site.tenant_code || '')
		|| !SITE_CODE_PATTERN.test(value.site.service_code || '')
		|| !value.endpoints
		|| typeof value.endpoints !== 'object'
	) {
		throw new Error('Invalid SSO public context.');
	}

	const endpoints = REQUIRED_ENDPOINTS.reduce((result, key) => {
		result[key] = validateSameOriginPath(value.endpoints[key], `endpoint ${key}`, origin);
		return result;
	}, {});

	return Object.freeze({
		enabled: true,
		available: true,
		unified_login_enabled: value.unified_login_enabled,
		passive_login_enabled: value.passive_login_enabled,
		active_sync_enabled: value.active_sync_enabled,
		site: Object.freeze({
			tenant_code: value.site.tenant_code,
			service_code: value.site.service_code,
		}),
		endpoints: Object.freeze(endpoints),
		cooldown_seconds: validatePositiveSeconds(value.cooldown_seconds, 'cooldown_seconds'),
		busy_ttl_seconds: validatePositiveSeconds(value.busy_ttl_seconds, 'busy_ttl_seconds'),
		passive_retry_seconds: validatePositiveSeconds(value.passive_retry_seconds, 'passive_retry_seconds'),
	});
}

function validateBootstrapPath(value, origin) {
	return validateSameOriginPath(value, 'bootstrap endpoint', origin);
}

function withFrontendLoginPath(context, configuredPath, origin) {
	const loginPath = validateSameOriginPath(configuredPath, 'frontend login path', origin);
	if (loginPath === '/api' || loginPath.startsWith('/api/')) {
		throw new Error('The frontend login path cannot target an API route.');
	}
	if (!context.enabled) return context;
	if (context.endpoints.login_path !== loginPath) {
		throw new Error('SSO frontend login path does not match the public context.');
	}

	return context;
}

module.exports = {
	disabledContext,
	unwrapResponse,
	validateBootstrapPath,
	validatePortalContext,
	validateSameOriginPath,
	withFrontendLoginPath,
};
